/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Grid,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Box,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IFlightService, IFlightServicePassengersByPassengerType } from 'features/salesOrder/types';
import { getProviderIssueCostRules } from 'features/salesOrder/services';

interface IssueCostCondition {
	providerId: string;
	validatingCarrier: string;
	tariffType: string;
	commission: number;
	over: number;
	feePercentage: number;
	feeValue: number;
	applyMinimum: boolean;
}

interface PassengerGroupCalculation {
	passengerType: string;
	originalCost: number;
	tariffPlusImp: number;
	tariff: number;
	calculatedCost: number;
}

interface Props {
	open: boolean;
	onClose: () => void;
	service: IFlightService;
	onSave: (updatedGroups: IFlightServicePassengersByPassengerType[]) => void;
	oidIssuesList?: Array<{ id: string | number; name: string }>;
}

export const IssueCostCalculatorModal: React.FC<Props> = ({ open, onClose, service, onSave, oidIssuesList = [] }) => {
	const [selectedTariffType, setSelectedTariffType] = useState<string>('');
	const [passengerGroupCalculations, setPassengerGroupCalculations] = useState<PassengerGroupCalculation[]>([]);
	const [tariffInputErrors, setTariffInputErrors] = useState<boolean[]>([]);
	const [issueCostConditions, setIssueCostConditions] = useState<IssueCostCondition[]>([]);
	const [isLoadingConditions, setIsLoadingConditions] = useState<boolean>(false);
	// Si la carga de reglas terminó y cómo: el aviso de "no tiene reglas" solo sale con una carga buena.
	const [conditionsLoad, setConditionsLoad] = useState<'pending' | 'ok' | 'error'>('pending');
	const tariffTypeSelectRef = useRef<HTMLSelectElement>(null);

	// Obtener el ID del proveedor
	const providerId = useMemo(() => {
		if (typeof service.tariffProvider === 'string') {
			return service.tariffProvider;
		}
		return service.tariffProvider?.id || '';
	}, [service.tariffProvider]);

	// Obtener el código del validating carrier
	const validatingCarrierCode = useMemo(() => {
		if (Array.isArray(service.vc) && service.vc.length > 0) {
			return service.vc[0].code;
		} else if (service.vc && typeof service.vc === 'object' && 'code' in service.vc) {
			return (service.vc as { code: string }).code;
		}
		return '';
	}, [service.vc]);

	// Cargar condiciones del API cuando se abre el modal
	useEffect(() => {
		if (open && issueCostConditions.length === 0) {
			setIsLoadingConditions(true);
			getProviderIssueCostRules()
				.then((response) => {
					setIssueCostConditions(response.data);
					setConditionsLoad('ok');
				})
				.catch((error) => {
					console.error('Error loading issue cost conditions:', error);
					setConditionsLoad('error');
				})
				.finally(() => {
					setIsLoadingConditions(false);
				});
		}
	}, [open, issueCostConditions.length]);

	// Las condiciones que pueden calcular para el proveedor y el VC actual, con la misma jerarquía que findCondition:
	// las del VC, las de ALL y, solo si el VC no tiene reglas propias, las de REST. Una de REST con reglas propias nunca
	// calcula: ofrecerla sumaba una opción que deja el costo igual.
	const availableConditions = useMemo(() => {
		const hasSpecificConditions = issueCostConditions.some(
			(condition: IssueCostCondition) =>
				condition.providerId === providerId && condition.validatingCarrier === validatingCarrierCode,
		);
		return issueCostConditions.filter(
			(condition: IssueCostCondition) =>
				condition.providerId === providerId &&
				(condition.validatingCarrier === validatingCarrierCode ||
					condition.validatingCarrier === 'ALL' ||
					(!hasSpecificConditions && condition.validatingCarrier === 'REST')),
		);
	}, [issueCostConditions, providerId, validatingCarrierCode]);

	// Tipos de tarifa únicos de esas condiciones
	const availableTariffTypes = useMemo(() => {
		const tariffTypes = availableConditions.map((condition: IssueCostCondition) => condition.tariffType);
		return Array.from(new Set(tariffTypes));
	}, [availableConditions]);

	// Función para extraer valores FARE F USD del campo tqt
	const extractFareValues = (tqtText: string): number[] => {
		if (!tqtText) return [];

		// Regex para encontrar "FARE F USD" seguido de espacios y un número
		const fareRegex = /FARE\s+F\s+USD\s+(\d+\.?\d*)/g;
		const fareValues: number[] = [];
		let match;

		while ((match = fareRegex.exec(tqtText)) !== null) {
			const value = parseFloat(match[1]);
			if (!isNaN(value)) {
				fareValues.push(value);
			}
		}

		console.log('=== EXTRACTED FARE VALUES ===');
		console.log('TQT Text exists:', !!tqtText);
		console.log('Extracted FARE values:', fareValues);
		console.log('=== END EXTRACTION ===');

		return fareValues;
	};

	// Inicializar cálculos cuando se abre el modal
	useEffect(() => {
		if (open && service.passengersGroupByPassengerType.length > 0 && issueCostConditions.length > 0) {
			console.log('=== MODAL INITIALIZATION ===');
			console.log('Provider ID:', providerId);
			console.log('VC Code:', validatingCarrierCode);
			console.log('Available conditions:', availableConditions);
			console.log('Available tariff types:', availableTariffTypes);

			// Extraer valores FARE del tqt si existe
			const fareValues = service.tqt ? extractFareValues(service.tqt) : [];

			// Con un solo tipo de tarifa se elige solo; con varios, lo elige el operador. Antes se tomaba el primero, que
			// depende del orden en que la API devuelve las reglas (el orden físico de la tabla, que cambia al editarlas).
			const firstAvailableTariffType = availableTariffTypes.length === 1 ? availableTariffTypes[0] : '';
			setSelectedTariffType(firstAvailableTariffType);

			const initialCalculations = service.passengersGroupByPassengerType.map((group, index) => {
				// Usar valor FARE extraído si está disponible para este índice, sino usar 0
				const tariffValue = fareValues[index] || 0;

				// Calcular el costo si tenemos tarifa prepopulada y tipo de tarifa
				const calculatedCost =
					tariffValue > 0 && firstAvailableTariffType
						? calculateCosts(group.cost, tariffValue, firstAvailableTariffType)
						: group.cost;

				return {
					passengerType: group.passengerType,
					originalCost: group.cost,
					tariffPlusImp: group.cost,
					tariff: tariffValue, // Prepopular con valor FARE si existe
					calculatedCost: calculatedCost, // Recalcular con la nueva tarifa si existe
				};
			});
			setPassengerGroupCalculations(initialCalculations);

			// Inicializar array de errores
			setTariffInputErrors(new Array(service.passengersGroupByPassengerType.length).fill(false));

			// Hacer focus en el select de tipo de tarifa después de un pequeño delay
			setTimeout(() => {
				if (tariffTypeSelectRef.current) {
					tariffTypeSelectRef.current.focus();
				}
			}, 100);
		}
	}, [
		open,
		service.passengersGroupByPassengerType,
		availableTariffTypes,
		providerId,
		validatingCarrierCode,
		availableConditions,
		issueCostConditions.length,
	]);

	// Función para buscar condición específica con lógica jerárquica
	const findCondition = (providerId: string, vcCode: string, tariffType: string): IssueCostCondition | null => {
		console.log('=== FINDING CONDITION ===');
		console.log('Looking for:', { providerId, vcCode, tariffType });
		console.log('Available conditions:', issueCostConditions);

		// 1. Buscar condición específica: Proveedor + VC específico + Tipo de tarifa
		let found = issueCostConditions.find(
			(condition: IssueCostCondition) =>
				condition.providerId === providerId &&
				condition.validatingCarrier === vcCode &&
				condition.tariffType === tariffType,
		);

		if (found) {
			console.log('Found specific condition:', found);
			console.log('=== END FINDING CONDITION ===');
			return found;
		}

		// 2. Buscar condición "ALL": Proveedor + "ALL" + Tipo de tarifa
		found = issueCostConditions.find(
			(condition: IssueCostCondition) =>
				condition.providerId === providerId &&
				condition.validatingCarrier === 'ALL' &&
				condition.tariffType === tariffType,
		);

		if (found) {
			console.log('Found ALL condition:', found);
			console.log('=== END FINDING CONDITION ===');
			return found;
		}

		// 3. Buscar condición "REST": Proveedor + "REST" + Tipo de tarifa
		// Solo aplica si NO existe una condición específica para este proveedor + VC
		const hasSpecificCondition = issueCostConditions.some(
			(condition: IssueCostCondition) => condition.providerId === providerId && condition.validatingCarrier === vcCode,
		);

		if (!hasSpecificCondition) {
			found = issueCostConditions.find(
				(condition: IssueCostCondition) =>
					condition.providerId === providerId &&
					condition.validatingCarrier === 'REST' &&
					condition.tariffType === tariffType,
			);

			if (found) {
				console.log('Found REST condition:', found);
				console.log('=== END FINDING CONDITION ===');
				return found;
			}
		} else {
			console.log('Specific condition exists for this provider + VC, skipping REST');
		}

		console.log('No condition found');
		console.log('=== END FINDING CONDITION ===');
		return null;
	};

	// Calcular costos según las fórmulas de Excel
	const calculateCosts = (tariffPlusImp: number, tariff: number, tariffType: string) => {
		const condition = findCondition(providerId, validatingCarrierCode, tariffType);
		if (!condition) return tariffPlusImp;

		console.log('=== DEBUGGING CALCULATECOSTS ===');
		console.log('Inputs:', { tariffPlusImp, tariff, tariffType });
		console.log('Condition found:', condition);

		// F2: comision = BUSCARV(CONCATENAR(A2;B2;C2);condiciones!A2:I;5;0)*(D2-G2)
		const overCom = condition.over * tariff; // G2: OverCom
		const commission = condition.commission * (tariff - overCom); // F2

		console.log('Calculated values:', { overCom, commission });

		// H2: Costo de emision
		const baseCalculation = (tariffPlusImp - commission - overCom) * condition.feePercentage;
		let issueCost: number;

		if (condition.applyMinimum) {
			issueCost = baseCalculation > condition.feeValue ? baseCalculation : condition.feeValue;
		} else {
			issueCost = condition.feeValue === 0 ? baseCalculation : condition.feeValue;
		}

		console.log('Issue cost calculation:', { baseCalculation, issueCost });

		// I2: Valor final
		const baseResult = tariffPlusImp - commission - overCom + issueCost;

		// Aplicar redondeo hacia arriba si no es IATA UY o IATA PY
		const providerName =
			typeof service.tariffProvider === 'string' ? service.tariffProvider : service.tariffProvider?.fantasyName || '';

		const isIATAUYorPY =
			providerName.toUpperCase().includes('IATA UY') ||
			providerName.toUpperCase().includes('IATA PY') ||
			providerId === 'iata-uy' ||
			providerId === 'iata-py';

		const finalResult = isIATAUYorPY ? baseResult : Math.ceil(baseResult);

		console.log('Final result:', { baseResult, finalResult });
		console.log('=== END DEBUGGING ===');

		return finalResult;
	};

	// Manejar cambios en los inputs
	const handleInputChange = (index: number, field: 'tariffPlusImp' | 'tariff', value: number) => {
		const updatedCalculations = [...passengerGroupCalculations];
		updatedCalculations[index][field] = value;

		// Limpiar error si el campo tariff tiene valor
		if (field === 'tariff' && value > 0) {
			const updatedErrors = [...tariffInputErrors];
			updatedErrors[index] = false;
			setTariffInputErrors(updatedErrors);
		}

		// Recalcular costo
		const calculatedCost = calculateCosts(
			updatedCalculations[index].tariffPlusImp,
			updatedCalculations[index].tariff,
			selectedTariffType,
		);
		updatedCalculations[index].calculatedCost = calculatedCost;

		setPassengerGroupCalculations(updatedCalculations);
	};

	// Manejar cambio de tipo de tarifa
	const handleTariffTypeChange = (newTariffType: string) => {
		setSelectedTariffType(newTariffType);

		// Recalcular todos los costos con el nuevo tipo de tarifa
		const updatedCalculations = passengerGroupCalculations.map((calc) => ({
			...calc,
			calculatedCost: calculateCosts(calc.tariffPlusImp, calc.tariff, newTariffType),
		}));
		setPassengerGroupCalculations(updatedCalculations);
	};

	// Función para calcular markup (réplica de la función calculatMarkupPercentaje)
	const calculateMarkupPercentage = (cost: number, saleAmount: number): number => {
		if (cost === 0) return 0;
		return parseFloat((((saleAmount - cost) / cost) * 100).toFixed(2));
	};

	// Con varios tipos de tarifa no hay uno elegido de antemano: sin elegir, no se aplica nada.
	const mustChooseTariffType = availableTariffTypes.length > 1 && !selectedTariffType;

	// Guardar cambios
	const handleSave = () => {
		// Validar que todos los campos de tarifa tengan valor
		const errors = passengerGroupCalculations.map((calc) => calc.tariff <= 0);
		const hasErrors = errors.some((error) => error);

		if (hasErrors) {
			setTariffInputErrors(errors);
			return;
		}

		const updatedGroups = service.passengersGroupByPassengerType.map((group, index) => {
			const newCost = passengerGroupCalculations[index]?.calculatedCost || group.cost;
			const newTotalCost = newCost * group.passengerIds.length;

			// Recalcular markup si hay venta > 0
			let newMarkupPercentage = group.markupPercentage;
			if (group.saleAmount > 0) {
				newMarkupPercentage = calculateMarkupPercentage(newCost, group.saleAmount);
			}

			return {
				...group,
				cost: newCost,
				totalCost: newTotalCost,
				markupPercentage: newMarkupPercentage,
			};
		});

		onSave(updatedGroups);
		onClose();
	};

	const providerName =
		typeof service.tariffProvider === 'string' ? service.tariffProvider : service.tariffProvider?.fantasyName || 'N/A';

	const vcName =
		Array.isArray(service.vc) && service.vc.length > 0
			? service.vc[0].name
			: service.vc && typeof service.vc === 'object' && 'name' in service.vc
			? (service.vc as { name: string }).name
			: 'N/A';

	// Obtener el nombre del OID de emisión
	const oidName = useMemo(() => {
		if (!service.oidIssueId || service.oidIssueId === 'n/a') return 'N/A';
		const oidItem = oidIssuesList.find((item) => item.id.toString() === service.oidIssueId);
		return oidItem ? oidItem.name : service.oidIssueId;
	}, [service.oidIssueId, oidIssuesList]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h6">Cálculo de costos de emisión</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent>
				{isLoadingConditions ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
						<CircularProgress />
						<Typography variant="body1" sx={{ ml: 2 }}>
							Cargando reglas de costo de emisión...
						</Typography>
					</Box>
				) : (
					<>
						<Grid container spacing={2} sx={{ mb: 3 }}>
							<Grid item xs={12} sm={4}>
								<Typography variant="subtitle2" color="text.secondary">
									Proveedor
								</Typography>
								<Typography variant="body1">{providerName}</Typography>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Typography variant="subtitle2" color="text.secondary">
									Validating Carrier
								</Typography>
								<Typography variant="body1">{vcName}</Typography>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Typography variant="subtitle2" color="text.secondary">
									OID de emisión
								</Typography>
								<Typography variant="body1">{oidName}</Typography>
							</Grid>
						</Grid>

						<Grid container spacing={2} sx={{ mb: 3 }}>
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel>Tipo de tarifa</InputLabel>
									<Select
										ref={tariffTypeSelectRef}
										value={selectedTariffType}
										onChange={(e) => handleTariffTypeChange(e.target.value as string)}
										label="Tipo de tarifa"
										autoFocus
									>
										{availableTariffTypes.map((type) => (
											<MenuItem key={type} value={type}>
												{type}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						</Grid>

						{mustChooseTariffType && (
							<Alert severity="info" sx={{ mb: 3 }}>
								Hay {availableTariffTypes.length} tipos de tarifa para {vcName}: elegí el que corresponde al boleto para
								calcular el costo.
							</Alert>
						)}
						{conditionsLoad === 'error' && (
							<Alert severity="error" sx={{ mb: 3 }}>
								No se pudieron cargar las reglas de costo de emisión: el costo no cambia. Cerrá y volvé a abrir la
								calculadora.
							</Alert>
						)}
						{conditionsLoad === 'ok' && providerId && validatingCarrierCode && availableTariffTypes.length === 0 && (
							<Alert severity="warning" sx={{ mb: 3 }}>
								{providerName} no tiene reglas de costo de emisión para {vcName}: el costo no cambia. Pedile a un
								administrador que las cargue en Reglas de emisión → Costos de emisión.
							</Alert>
						)}

						<TableContainer component={Paper}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Grupo</TableCell>
										<TableCell align="right">Costo Item</TableCell>
										<TableCell align="right">Tarifa + Imp</TableCell>
										<TableCell align="right">Tarifa</TableCell>
										<TableCell align="right">Costo para actualizar</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{passengerGroupCalculations.map((calc, index) => (
										<TableRow key={calc.passengerType}>
											<TableCell>
												<Typography variant="body2" fontWeight="bold">
													Grupo {calc.passengerType}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Typography variant="body2">{calc.originalCost.toFixed(2)}</Typography>
											</TableCell>
											<TableCell align="right">
												<TextField
													size="small"
													type="number"
													value={calc.tariffPlusImp}
													onChange={(e) => handleInputChange(index, 'tariffPlusImp', parseFloat(e.target.value) || 0)}
													inputProps={{ step: 0.01, min: 0 }}
													sx={{ width: 100 }}
												/>
											</TableCell>
											<TableCell align="right">
												<TextField
													size="small"
													type="number"
													value={calc.tariff || ''}
													onChange={(e) => handleInputChange(index, 'tariff', parseFloat(e.target.value) || 0)}
													inputProps={{ step: 0.01, min: 0 }}
													sx={{ width: 100 }}
													error={tariffInputErrors[index]}
													helperText={tariffInputErrors[index] ? 'Requerido' : ''}
													placeholder="0.00"
												/>
											</TableCell>
											<TableCell align="right">
												<Typography variant="body2" fontWeight="bold" color="primary">
													{calc.calculatedCost.toFixed(2)}
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} color="secondary">
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					color="primary"
					disabled={isLoadingConditions || mustChooseTariffType}
				>
					Aplicar modificación y guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
